import React, { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { FaEye, FaPencilAlt, FaMinusCircle, FaPlus, FaSpinner, FaUserMd } from 'react-icons/fa';
import ProviderModal from '../../components/ProviderModal';
import Chip from '../../components/Chip';
import { Provider, Facility } from '../../types';

const FacilityProviders = () => {
	const { token } = useAuth();
	const [searchTerm, setSearchTerm] = useState('');
	const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'ascending' | 'descending' }>({ key: null, direction: 'ascending' });
	const [activeTab, setActiveTab] = useState('All');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
	const [providers, setProviders] = useState<Provider[]>([]);
	const [facilities, setFacilities] = useState<Facility[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);        const tabs = [
                { id: 'All', label: 'All' },
                { id: 'Active', label: 'Active' },
                { id: 'Inactive', label: 'Inactive' },
        ];

        // Load data when auth token is available
        useEffect(() => {
                if (!token) return;
                loadProviders();
                loadFacilities();
        }, [token]);

        const loadProviders = async () => {
                if (!token) return;
                setIsLoading(true);
                try {
                        const response = await apiCall('/providers/providers/', {
                                method: 'GET'
                        }, token);
                        if (response.ok) {
                                const data = await response.json();
                                setProviders(data.results || data);
                                setError(null);
                        } else {
                                const errorText = await response.text();
                                throw new Error(`Failed to load providers (${response.status}): ${errorText || response.statusText}`);
                        }
                } catch (error) {
                        console.error('Error loading providers:', error);
                        setError(error instanceof Error ? error.message : 'Failed to load providers');
                } finally {
                        setIsLoading(false);
                }
        };

        const loadFacilities = async () => {
                if (!token) return;
                try {
                        const response = await apiCall('/facilities/facilities/', {
                                method: 'GET'
                        }, token);
                        if (response.ok) {
                                const data = await response.json();
                                setFacilities(data.results || data);
                        } else {
                                const errorText = await response.text();
                                console.error(`Failed to load facilities (${response.status}):`, errorText || response.statusText);
                        }
                } catch (error) {
                        console.error('Error loading facilities:', error);
                }
        };

        const handleAddProvider = () => {
                setEditingProvider(null);
                setIsModalOpen(true);
        };

        const handleEditProvider = (provider: Provider) => {
                setEditingProvider(provider);
                setIsModalOpen(true);
        };

        const handleSaveProvider = async (providerData: Partial<Provider>) => {
                try {
                        const url = editingProvider 
                                ? `/providers/providers/${editingProvider.id}/`
                                : '/providers/providers/';
                        
                        const method = editingProvider ? 'PUT' : 'POST';
                        
                        const response = await apiCall(url, {
                                method,
                                headers: {
                                        'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(providerData),
                        }, token || undefined);

                        if (response.ok) {
                                await loadProviders(); // Reload the providers list
                                setIsModalOpen(false);
                                setEditingProvider(null);
                        } else {
                                const errorData = await response.json();
                                throw new Error(errorData.message || 'Failed to save provider');
                        }
                } catch (error) {
                        console.error('Error saving provider:', error);
                        throw error; // Re-throw to let the modal handle the error
                }
        };

        const handleDeactivateProvider = async (providerId: number | string) => {
                if (!confirm('Are you sure you want to deactivate this provider?')) {
                        return;
                }

                try {
                        const response = await apiCall(`/providers/providers/${providerId}/`, {
                                method: 'PATCH',
                                headers: {
                                        'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ is_active: false }),
                        }, token || undefined);

                        if (response.ok) {
                                await loadProviders();
                        } else {
                                throw new Error('Failed to deactivate provider');
                        }
                } catch (error) {
                        console.error('Error deactivating provider:', error);
                        alert('Failed to deactivate provider');
                }
        };

        const requestSort = (key: string) => {
                let direction: 'ascending' | 'descending' = 'ascending';
                if (sortConfig.key === key && sortConfig.direction === 'ascending') {
                        direction = 'descending';
                }
                setSortConfig({ key, direction });
        };

        const formatPhoneNumber = (phone: string) => {
                // Remove any non-digit characters
                const cleaned = phone.replace(/\D/g, '');

                // Check if it's a mobile number (starts with 04)
                if (cleaned.startsWith('04')) {
                        return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
                }
                // Check if it's a landline (starts with 02, 03, 07, or 08)
                else if (/^0[2378]/.test(cleaned)) {
                        return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
                }
                // Return original number if it doesn't match any pattern
                return phone;
        };

        const getLocationBadge = (location: string) => {
                // Color mapping for different states
                const stateColors: Record<string, string> = {
                        'VIC': 'bg-blue-100 text-blue-800',
                        'NSW': 'bg-red-100 text-red-800',
                        'QLD': 'bg-amber-100 text-amber-800'
                };

                // Determine the state from the location string
                const state = location.split(', ')[1];
                const colorClass = stateColors[state] || 'bg-gray-100 text-gray-800';

                return (
                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${colorClass} mr-2 mb-1`}>
                                {location}
                        </span>
                );
        };

        const getSortedProviders = () => {
                let filteredProviders = [...providers];

                if (activeTab !== 'All') {
                        const isActive = activeTab === 'Active';
                        filteredProviders = filteredProviders.filter(provider => provider.is_active === isActive);
                }

                if (searchTerm) {
                        filteredProviders = filteredProviders.filter(provider =>
                                Object.values(provider).some(value =>
                                        String(value).toLowerCase().includes(searchTerm.toLowerCase())
                                )
                        );
                }

                if (sortConfig.key) {
                        filteredProviders.sort((a, b) => {
                                let aValue = a[sortConfig.key as keyof Provider];
                                let bValue = b[sortConfig.key as keyof Provider];
                                
                                // Handle nested properties
                                if (sortConfig.key === 'full_name') {
                                        aValue = `${a.first_name} ${a.last_name}`;
                                        bValue = `${b.first_name} ${b.last_name}`;
                                }
                                
                                // Handle undefined values
                                if (aValue === undefined && bValue === undefined) return 0;
                                if (aValue === undefined) return 1;
                                if (bValue === undefined) return -1;
                                
                                if (aValue < bValue) {
                                        return sortConfig.direction === 'ascending' ? -1 : 1;
                                }
                                if (aValue > bValue) {
                                        return sortConfig.direction === 'ascending' ? 1 : -1;
                                }
                                return 0;
                        });
                }

                return filteredProviders;
        };

        return (
                <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                <FaUserMd className="text-primary" />
                                Providers
                        </h1>
                        <button 
                                className="btn btn-primary"
                                onClick={handleAddProvider}
                                disabled={isLoading}
                        >
                                <FaPlus className="mr-2" />
                                Add Provider
                        </button>
                </div>                        <div className="mb-6 flex items-center space-x-4">
                                <div className="join">
                                        <div className="relative join-item">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                        <svg className="w-4 h-4 text-base-content/60" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                                                        </svg>
                                                </div>
                                                <input
                                                        type="text"
                                                        placeholder="Search providers..."
                                                        className="input input-bordered w-64 pl-10 join-item"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                        </div>
                                        <select className="select select-bordered join-item">
                                                <option value="">Specialty</option>
                                        </select>
                                        <select className="select select-bordered join-item">
                                                <option value="">Location</option>
                                        </select>
                                        <button className="btn btn-primary join-item">Search</button>
                                </div>
                        </div>

                        <div className="tabs tabs-bordered mb-6">
                                {tabs.map((tab) => (
                                        <button
                                                key={tab.id}
                                                className={`tab tab-lg ${activeTab === tab.id ? 'tab-active' : ''}`}
                                                onClick={() => setActiveTab(tab.id)}
                                        >
                                                {tab.label}
                                                {tab.id !== 'All' && !isLoading && (
                                                        <span className="ml-2">
                                                                <Chip label={providers.filter(p => p.is_active === (tab.id === 'Active')).length.toString()} tone="neutral" />
                                                        </span>
                                                )}
                                        </button>
                                ))}
                        </div>

                        {error && (
                                <div className="alert alert-error mb-6">
                                        <span>{error}</span>
                                </div>
                        )}

                        {isLoading ? (
                                <div className="flex justify-center items-center h-64">
                                        <FaSpinner className="animate-spin text-4xl" />
                                        <span className="ml-4 text-lg">Loading providers...</span>
                                </div>
                        ) : (

                        <div className="relative overflow-x-auto rounded-lg">
                                <table className="table table-zebra bg-base-100/50">
                                        <thead>
                                                <tr>
                                                        <th>First Name</th>
                                                        <th>Last Name</th>
                                                        <th>Provider Type</th>
                                                        <th>Specialty</th>
                                                        <th>Contact</th>
                                                        <th>Facilities</th>
                                                        <th>Performs Procedures</th>
                                                        <th>Commands</th>
                                                </tr>
                                        </thead>
                                        <tbody>
                                                {getSortedProviders().map((provider) => (
                                                        <tr key={provider.id}>
                                                                <td>
                                                                        <Link
                                                                                to={`/clinic/providers/${provider.id}`}
                                                                                className="hover:underline cursor-pointer"
                                                                        >
                                                                                {provider.first_name || 'N/A'}
                                                                        </Link>
                                                                </td>
                                                                <td>{provider.last_name || 'N/A'}</td>
                                                                <td>{provider.provider_type_display || 'N/A'}</td>
                                                                <td>
                                                                        {provider.specializations_details?.map(s => s.name).join(', ') || 'None'}
                                                                </td>
                                                                <td>
                                                                        <div>{formatPhoneNumber(provider.phone || '')}</div>
                                                                        <div className="text-sm opacity-60">{provider.email || 'No email'}</div>
                                                                </td>
                                                                <td>
                                                                        <div className="flex flex-wrap gap-1">
                                                                                {provider.facilities && provider.facilities.length > 0 ? (
                                                                                        provider.facilities.map((location) => (
                                                                                                <Chip
                                                                                                        key={location.id}
                                                                                                        label={location.name}
                                                                                                        color={location.color_code || '#808080'}
                                                                                                        title={location.name}
                                                                                                />
                                                                                        ))
                                                                                ) : (
                                                                                        <span className="text-xs opacity-60">No facilities set</span>
                                                                                )}
                                                                        </div>
                                                                </td>
                                                                <td className="text-center">
                                                                        {provider.performs_procedures ? (
                                                                                <Chip label="Yes" tone="success" />
                                                                        ) : (
                                                                                <Chip label="No" tone="neutral" />
                                                                        )}
                                                                </td>
                                                                <td>
                                                                        <div className="flex gap-2">
                                                                                <Link to={`/clinic/providers/${provider.id}`} className="btn btn-ghost btn-xs">
                                                                                        <FaEye className="w-4 h-4" />
                                                                                </Link>
                                                                                <button 
                                                                                        className="btn btn-ghost btn-xs"
                                                                                        onClick={() => handleEditProvider(provider)}
                                                                                >
                                                                                        <FaPencilAlt className="w-4 h-4" />
                                                                                </button>
                                                                                <button
                                                                                        className="btn btn-ghost btn-xs"
                                                                                        onClick={() => handleDeactivateProvider(provider.id)}
                                                                                        disabled={!provider.is_active}
                                                                                >
                                                                                        <FaMinusCircle className="w-4 h-4" />
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                ))}
                                        </tbody>
                                </table>
                        </div>
                        )}

                        {/* Provider Modal */}
                        <ProviderModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                onSave={handleSaveProvider}
                                editProvider={editingProvider}
                                facilities={facilities}
                                token={token}
                        />
                </div>
        );
};

export default FacilityProviders;